/*
 * Maps where the member is in the app (route + topmost open drawer) to help-article tags,
 * so the help widget can show "help for this page" without a search.
 *
 * The tag names are the curated help-center vocabulary (customer.featureFlags.helpCenterTags)
 * plus the AI topic tags in use on the help articles. The article GET endpoint matches them
 * against both aiTopicTags and the manual tags.name, so either kind of tagging works.
 *
 * Everything here is pure: the maps are never mutated and every call returns a fresh array.
 */

// Drawer component name → tags. A drawer is what the member is actually looking at, so it
// takes precedence over the route underneath it.
const DRAWER_TAGS = Object.freeze({
    reports: ['Rapporter'],

    salePayment: ['Betalinger'],
    editSalePayment: ['Betalinger'],
    netsPayment: ['Betalinger'],
    saleInvoice: ['Betalinger'],
    sendInvoice: ['Betalinger'],
    sendFriendlyReminder: ['Betalinger'],

    editJournal: ['Journaler'],
    newJournal: ['Journaler'],
    viewJournal: ['Journaler'],
    journals: ['Journaler'],
    miniJournal: ['Journaler'],
    sendJournal: ['Journaler'],
    journalSettings: ['Journaler'],
    addDiagnose: ['Journaler'],
    miniDiagnoses: ['Journaler'],

    addMedicine: ['Medisiner'],
    miniMedicine: ['Medisiner'],
    allMedicine: ['Medisiner'],
    newDrug: ['Medisiner'],

    addPrescription: ['Resepter'],
    miniPrescription: ['Resepter'],
    viewPrescription: ['Resepter'],
    emailPrescription: ['Resepter'],
    viewAnimalPrescriptions: ['Resepter'],

    editAnimal: ['Dyr'],
    addAnimal: ['Dyr'],
    addAnimalDhp: ['Dyr'],
    viewAnimal: ['Dyr'],
    miniAnimal: ['Dyr'],
    viewAnimalJournals: ['Dyr'],
    aiAnimalSummary: ['Dyr'],
    dhpHealthHistory: ['Dyr'],

    editVisitor: ['Kunder'],
    newVisitor: ['Kunder'],
    editContact: ['Kunder'],
    newContact: ['Kunder'],

    editProduct: ['Produkter'],
    newProduct: ['Produkter'],
    allProducts: ['Produkter'],
    productStockAdjustment: ['Produkter'],
    productPurchaseDetails: ['Produkter'],
    scanBarcode: ['Produkter'],

    sendSms: ['SMS/E-post'],
    sendBulkSms: ['SMS/E-post'],
    sendEmail: ['SMS/E-post'],
    viewSms: ['SMS/E-post'],
    viewMailSent: ['SMS/E-post'],

    newCalendarEvent: ['Kalender'],
    viewCalendarEvent: ['Kalender'],
    editCalendar: ['Kalender'],
    newCalendar: ['Kalender'],

    newFile: ['Filer'],
    editFile: ['Filer'],
    listFileTable: ['Filer'],
    fileViewer: ['Filer'],

    idexxOrderForm: ['Integrering'],
    viewAnimalIdexxResults: ['Integrering'],
    sendInsuranceClaim: ['Integrering'],

    allVisits: ['Besøk'],

    editFieldsSettings: ['Innstillinger'],
    editCustomer: ['Innstillinger'],
    editCustomerUser: ['Innstillinger'],
    newCustomerUser: ['Innstillinger'],

    sendCourseExam: ['Kurs', 'Prøve'],
    editCourse: ['Kurs'],
    newCourse: ['Kurs'],
});

// Route prefix → tags. Matched on path boundaries; the longest matching prefix wins.
const ROUTE_TAGS = Object.freeze({
    '/dyrejournal-sale': ['Salg', 'Besøk'],
    '/dyrejournal-mobile': ['Salg', 'Besøk'],
    '/dyrejournal-payments': ['Betalinger'],
    '/dyrejournal-pending-sales': ['Salg', 'Betalinger'],
    '/dyrejournal-sms': ['SMS/E-post'],
    '/dyrejournal-sms-reminder-log': ['SMS/E-post'],
    '/dyrejournal-mailsent': ['SMS/E-post'],
    '/texttemplates': ['SMS/E-post'],
    '/dyrejournal-files': ['Filer'],
    '/dyrejournal-contacts': ['Kunder'],
    '/visitor': ['Kunder'],
    '/dyrejournal-phone-consultations': ['Besøk'],
    '/dyrejournal-prescription-orders': ['Resepter'],
    '/dyrejournal-clinic': ['Innstillinger'],
    '/dyrejournal-lab-results': ['Integrering'],
    '/dyrejournal-idexx-mapping': ['Integrering'],
    '/admin/idexx': ['Integrering'],
    '/dyrejournal-kpi': ['Rapporter'],
    '/dyrejournal-accounts': ['Rapporter'],
    '/animal': ['Dyr'],
    '/calendar': ['Kalender'],
    '/products': ['Produkter'],
    '/pricelists': ['Produkter'],
    '/dyrejournal-admin': ['Innstillinger', 'Brukere'],
    '/admin': ['Innstillinger', 'Brukere'],
    '/users/profile': ['Brukere'],
    '/exam': ['Prøve', 'Diplom', 'Kurs'],
    '/elearning': ['Kurs', 'Prøve'],
    '/course': ['Kurs', 'Prøve'],
});

// Longest prefix first so "/dyrejournal-sms-reminder-log" beats "/dyrejournal-sms".
const ROUTE_PREFIXES = Object.freeze(Object.keys(ROUTE_TAGS).sort((a, b) => b.length - a.length));

const MAX_DRAWER_LEVEL = 7;

function prefixMatches(path, prefix) {
    if (!path.startsWith(prefix)) return false;
    const next = path.charAt(prefix.length);
    return next === '' || next === '/' || next === '?';
}

/**
 * Tags for the given location. Drawer first, then the longest matching route prefix,
 * otherwise an empty list. Always returns a new array.
 */
export function getHelpContextTags({ path = '', drawer = null } = {}) {
    if (drawer && DRAWER_TAGS[drawer]) {
        return [...DRAWER_TAGS[drawer]];
    }
    const cleanPath = String(path || '');
    const prefix = ROUTE_PREFIXES.find(p => prefixMatches(cleanPath, p));
    return prefix ? [...ROUTE_TAGS[prefix]] : [];
}

/**
 * Name of the topmost open drawer (left or right), or null. Reads appState without touching it.
 */
export function getTopDrawerComponent(appState = {}) {
    for (let level = MAX_DRAWER_LEVEL; level >= 1; level -= 1) {
        const shown = appState[`showDrawer${level}`] || appState[`showDrawerRight${level}`];
        const component = appState[`drawerComponent${level}`];
        if (shown && component) {
            return String(component);
        }
    }
    return null;
}

/**
 * Stable key for "the member is here", used to skip reloading when the panel is reopened
 * on the same page.
 */
export function getHelpContextKey({ path = '', drawer = null } = {}) {
    return drawer ? `${path}|${drawer}` : String(path || '');
}
